import React from "react";
import { GKLayoutShell } from "@granular/gds-ux-components";

import { ChildrenProp } from "../types";
import {
  useMaybeAuthToken,
  useMaybeLocale,
  useMaybeUserId,
  useMaybeCurrentOperationId,
  useMaybeAuthentication,
  useMaybeUser,
  useMaybeCurrentOperation,
  useMaybeOperations,
  useMaybeCrops,
  useMaybeFarms,
  useMaybeEnrollments,
  useMaybeFieldCrops,
  useMaybeFields,
  useMaybeFieldEnrollments,
  useMaybeInvites,
  useMaybeMembers,
  useMaybeFeatureFlags,
  useMaybeCurrentOperationV4,
} from "../next/hooks";
import BigGiantOops from "./Error";
import Loading from "./Loading";

/**
 * Define the data that must be loaded before your application mounts
 */
export type RequiredCoreData = Partial<{
  crops: boolean;
  enrollments: boolean;
  farms: boolean;
  fieldCrops: boolean;
  fieldEnrollments: boolean;
  fields: boolean;
  members: boolean;
  invites: boolean;
  integrationsFeatureFlags: boolean;
  agronomyFeatureFlags: boolean;
  activitiesFeatureFlags: boolean;
  analyzeFeatureFlags: boolean;
}>;

export type PropsOfEnsureCoreData = {
  requiredCoreData?: RequiredCoreData;
  isOutsideGKLayoutShell?: boolean;
} & ChildrenProp;

export type FabricApp = React.ComponentType &
  Partial<{
    requiredCoreData: RequiredCoreData;
  }>;

/**
 * This is a final sanity check to ensure all guaranteed data is present
 *
 * This also runs the full version of all hooks in order to ensure
 * the backwards compatible global status and activity for old hooks.
 */
const EnsureCoreData: React.FC<PropsOfEnsureCoreData> = ({
  children,
  requiredCoreData: _requiredCoreData,
  isOutsideGKLayoutShell,
}) => {
  React.useEffect(() => {
    console.debug("[@granular/fabric-core][EnsureCoreData] mounted");
  }, []);

  const includeByDefault = !_requiredCoreData;
  const requiredCoreData: Required<RequiredCoreData> = {
    crops: includeByDefault,
    enrollments: includeByDefault,
    farms: includeByDefault,
    fieldCrops: includeByDefault,
    fieldEnrollments: includeByDefault,
    fields: includeByDefault,
    members: includeByDefault,
    invites: includeByDefault,
    integrationsFeatureFlags: includeByDefault,
    agronomyFeatureFlags: includeByDefault,
    activitiesFeatureFlags: includeByDefault,
    analyzeFeatureFlags: includeByDefault,
    ..._requiredCoreData,
  };

  const authToken = useMaybeAuthToken();
  const locale = useMaybeLocale();
  const userId = useMaybeUserId();
  const currentOperationId = useMaybeCurrentOperationId();

  // Use the full hooks here to ensure our global status side effects run.
  // Once we no longer need to support those, we can just call the data hooks here.
  const auth = useMaybeAuthentication();
  const user = useMaybeUser();
  const currentOperation = useMaybeCurrentOperation();
  const currentOperationV4 = useMaybeCurrentOperationV4();
  const operations = useMaybeOperations();
  const crops = useMaybeCrops({ forceDisable: !requiredCoreData.crops });
  const enrollments = useMaybeEnrollments({
    forceDisable: !requiredCoreData.enrollments,
  });
  const farms = useMaybeFarms({ forceDisable: !requiredCoreData.farms });
  const fieldCrops = useMaybeFieldCrops({
    forceDisable: !requiredCoreData.fieldCrops,
  });
  const fieldEnrollments = useMaybeFieldEnrollments({
    forceDisable: !requiredCoreData.fieldEnrollments,
  });
  const fields = useMaybeFields({ forceDisable: !requiredCoreData.fields });
  const members = useMaybeMembers({ forceDisable: !requiredCoreData.members });
  const invites = useMaybeInvites({ forceDisable: !requiredCoreData.invites });

  const integrationsFeatureFlags = useMaybeFeatureFlags({
    app: "g-integrations",
    forceDisable: !requiredCoreData.integrationsFeatureFlags,
  });
  const insightsFeatureFlags = useMaybeFeatureFlags({
    app: "g_insights",
  });
  const agronomyFeatureFlags = useMaybeFeatureFlags({
    app: "agronomy",
    forceDisable: !requiredCoreData.agronomyFeatureFlags,
  });
  const activitiesFeatureFlags = useMaybeFeatureFlags({
    app: "activities",
    forceDisable: !requiredCoreData.activitiesFeatureFlags,
  });
  const analyzeFeatureFlags = useMaybeFeatureFlags({
    app: "analyze",
    forceDisable: !requiredCoreData.analyzeFeatureFlags,
  });
  const fabricFeatureFlags = useMaybeFeatureFlags({
    app: "fabric",
  });
  const documentServiceFeatureFlags = useMaybeFeatureFlags({
    app: "document-service",
  });

  const isError =
    auth.status === "error" ||
    user.status === "error" ||
    currentOperation.status === "error" ||
    currentOperationV4.status === "error" ||
    operations.status === "error" ||
    (requiredCoreData.crops && crops.status === "error") ||
    (requiredCoreData.enrollments && enrollments.status === "error") ||
    (requiredCoreData.farms && farms.status === "error") ||
    (requiredCoreData.fieldCrops && fieldCrops.status === "error") ||
    (requiredCoreData.fieldEnrollments &&
      fieldEnrollments.status === "error") ||
    (requiredCoreData.fields && fields.status === "error") ||
    (requiredCoreData.invites && invites.status === "error") ||
    (requiredCoreData.members && members.status === "error") ||
    (requiredCoreData.integrationsFeatureFlags &&
      integrationsFeatureFlags.status === "error") ||
    insightsFeatureFlags.status === "error" ||
    (requiredCoreData.agronomyFeatureFlags &&
      agronomyFeatureFlags.status === "error") ||
    (requiredCoreData.activitiesFeatureFlags &&
      activitiesFeatureFlags.status === "error") ||
    (requiredCoreData.analyzeFeatureFlags &&
      analyzeFeatureFlags.status === "error") ||
    fabricFeatureFlags.status === "error" ||
    documentServiceFeatureFlags.status === "error";

  const isSuccess =
    auth.status === "success" &&
    user.status === "success" &&
    currentOperation.status === "success" &&
    currentOperationV4.status === "success" &&
    operations.status === "success" &&
    (!requiredCoreData.crops || crops.status === "success") &&
    (!requiredCoreData.enrollments || enrollments.status === "success") &&
    (!requiredCoreData.farms || farms.status === "success") &&
    (!requiredCoreData.fieldCrops || fieldCrops.status === "success") &&
    (!requiredCoreData.fieldEnrollments ||
      fieldEnrollments.status === "success") &&
    (!requiredCoreData.fields || fields.status === "success") &&
    (!requiredCoreData.invites || invites.status === "success") &&
    (!requiredCoreData.members || members.status === "success") &&
    (!requiredCoreData.integrationsFeatureFlags ||
      integrationsFeatureFlags.status === "success") &&
    insightsFeatureFlags.status === "success" &&
    (!requiredCoreData.agronomyFeatureFlags ||
      agronomyFeatureFlags.status === "success") &&
    (!requiredCoreData.activitiesFeatureFlags ||
      activitiesFeatureFlags.status === "success") &&
    (!requiredCoreData.analyzeFeatureFlags ||
      analyzeFeatureFlags.status === "success") &&
    fabricFeatureFlags.status === "success" &&
    documentServiceFeatureFlags.status === "success";

  const isPresent = Boolean(
    authToken &&
      locale &&
      userId &&
      currentOperationId &&
      auth.data &&
      user.data &&
      currentOperation.data &&
      currentOperationV4.data &&
      operations.data &&
      (!requiredCoreData.crops || crops.data) &&
      (!requiredCoreData.enrollments || enrollments.data) &&
      (!requiredCoreData.farms || farms.data) &&
      (!requiredCoreData.fieldCrops || fieldCrops.data) &&
      (!requiredCoreData.fieldEnrollments || fieldEnrollments.data) &&
      (!requiredCoreData.fields || fields.data) &&
      (!requiredCoreData.invites || invites.data) &&
      (!requiredCoreData.members || members.data) &&
      (!requiredCoreData.integrationsFeatureFlags ||
        integrationsFeatureFlags.data) &&
      insightsFeatureFlags.data &&
      (!requiredCoreData.agronomyFeatureFlags || agronomyFeatureFlags.data) &&
      (!requiredCoreData.activitiesFeatureFlags ||
        activitiesFeatureFlags.data) &&
      (!requiredCoreData.analyzeFeatureFlags || analyzeFeatureFlags.data) &&
      fabricFeatureFlags.data &&
      documentServiceFeatureFlags.data,
  );

  if (isError) {
    console.error(
      "[@granular/fabric-core][EnsureCoreData] Error",
      requiredCoreData,
      {
        authToken,
        locale,
        userId,
        currentOperationId,
        authData: auth.data,
        userData: user.data,
        currentOperationData: currentOperation.data,
        currentOperationDataV4: currentOperationV4.data,
        operationsData: operations.data,
        cropsData: crops.data,
        enrollments: enrollments.data,
        farms: farms.data,
        fieldCrops: fieldCrops.data,
        fieldsEnrollments: fieldEnrollments.data,
        fields: fields.data,
        members: members.data,
        invites: invites.data,
        integrationsFeatureFlags: integrationsFeatureFlags.data,
        insightsFeatureFlags: insightsFeatureFlags.data,
        agronomyFeatureFlags: agronomyFeatureFlags.data,
        activitiesFeatureFlags: activitiesFeatureFlags.data,
        analyzeFeatureFlags: analyzeFeatureFlags.data,
        fabricFeatureFlags: fabricFeatureFlags.data,
        documentServiceFeatureFlags: documentServiceFeatureFlags.data,
      },
    );

    if (isOutsideGKLayoutShell) {
      return (
        <GKLayoutShell>
          <BigGiantOops useGKContent />
        </GKLayoutShell>
      );
    }

    return <BigGiantOops useGKContent />;
  }

  if (!isPresent || !isSuccess) {
    console.debug(
      "[@granular/fabric-core][EnsureCoreData] Loading",
      requiredCoreData,
      {
        authToken,
        locale,
        userId,
        currentOperationId,
        authData: auth.data,
        userData: user.data,
        currentOperationData: currentOperation.data,
        operationsData: operations.data,
        cropsData: crops.data,
        enrollments: enrollments.data,
        farms: farms.data,
        fieldCrops: fieldCrops.data,
        fieldsEnrollments: fieldEnrollments.data,
        fields: fields.data,
        members: members.data,
        invites: invites.data,
        integrationsFeatureFlags: integrationsFeatureFlags.data,
        insightsFeatureFlags: insightsFeatureFlags.data,
        agronomyFeatureFlags: agronomyFeatureFlags.data,
        activitiesFeatureFlags: activitiesFeatureFlags.data,
        analyzeFeatureFlags: analyzeFeatureFlags.data,
        fabricFeatureFlags: fabricFeatureFlags.data,
        documentServiceFeatureFlags: documentServiceFeatureFlags.data,
      },
    );

    if (isOutsideGKLayoutShell) {
      return (
        <GKLayoutShell>
          <Loading useGKContent />
        </GKLayoutShell>
      );
    }

    return <Loading useGKContent />;
  }

  return <>{children}</>;
};

export default EnsureCoreData;
